/**
 * Terms Service
 *
 * Manages Terms & Conditions and Privacy Policy acceptance:
 * - Fetching current terms version and content
 * - Recording acceptance for audit trail
 * - Checking if user needs to re-accept updated terms
 * - Getting user's acceptance history
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Default terms config (fallback if Firestore unavailable)
const DEFAULT_TERMS_CONFIG = {
  currentVersion: '1.0.0',
  requiredVersion: '1.0.0',
  privacyVersion: '1.0.0',
  effectiveDate: new Date()
};

/**
 * Get current terms configuration
 * @returns {Promise<Object>} Terms config with current/required versions
 */
export async function getCurrentTermsConfig() {
  try {
    const configRef = doc(db, 'siteConfig', 'terms');
    const configSnap = await getDoc(configRef);

    if (configSnap.exists()) {
      const data = configSnap.data();
      return {
        currentVersion: data.currentVersion || DEFAULT_TERMS_CONFIG.currentVersion,
        requiredVersion: data.requiredVersion || DEFAULT_TERMS_CONFIG.requiredVersion,
        privacyVersion: data.privacyVersion || DEFAULT_TERMS_CONFIG.privacyVersion,
        effectiveDate: data.effectiveDate?.toDate?.() || DEFAULT_TERMS_CONFIG.effectiveDate
      };
    }

    return DEFAULT_TERMS_CONFIG;
  } catch (error) {
    console.error('Error fetching terms config:', error);
    // Return default config instead of throwing - allows app to work without Firestore data
    return DEFAULT_TERMS_CONFIG;
  }
}

/**
 * Get terms or privacy policy content for a specific version
 * @param {string} version - Version to fetch (e.g., "1.0.0")
 * @param {string} type - "terms" or "privacy"
 * @returns {Promise<Object|null>} Terms content object or null if not found
 */
export async function getTermsContent(version, type = 'terms') {
  try {
    const versionId = `${type}_v${version}`;
    const versionRef = doc(db, 'termsVersions', versionId);
    const versionSnap = await getDoc(versionRef);

    if (versionSnap.exists()) {
      const data = versionSnap.data();
      return {
        version: data.version,
        type: data.type,
        title: data.title,
        content: data.content,
        summary: data.summary,
        effectiveDate: data.effectiveDate?.toDate?.(),
        createdAt: data.createdAt?.toDate?.(),
        previousVersion: data.previousVersion
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching terms content:', error);
    throw error;
  }
}

/**
 * Get client IP address
 * Note: IP capture is skipped on web due to CSP restrictions.
 * The audit trail still records all other acceptance data.
 * For full IP capture, this would need server-side implementation.
 * @returns {Promise<string|null>} IP address or null
 */
export async function getClientIP() {
  // Skip IP capture - CSP blocks external API calls
  // IP is optional for audit trail, all other data is still recorded
  return null;
}

/**
 * Record terms acceptance in audit trail
 * @param {Object} data - Acceptance data
 * @param {string} data.userId - User ID (null for guests)
 * @param {string} data.termsVersion - Terms version accepted
 * @param {string} data.privacyVersion - Privacy version accepted
 * @param {string} data.source - "web" | "ios" | "guest"
 * @param {string} data.context - "signup" | "login_reaccept" | "guest_meeting"
 * @param {string} [data.guestSessionId] - Guest session ID (for guests only)
 * @returns {Promise<string>} Acceptance ID
 */
export async function recordTermsAcceptance(data) {
  try {
    const { userId, termsVersion, privacyVersion, source, context, guestSessionId } = data;

    // Get client IP
    const ipAddress = await getClientIP();

    // Generate acceptance ID
    const acceptanceId = `${userId || 'guest'}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const acceptanceRef = doc(db, 'termsAcceptance', acceptanceId);
    await setDoc(acceptanceRef, {
      userId: userId || null,
      guestSessionId: guestSessionId || null,
      termsVersion,
      privacyVersion,
      acceptedAt: serverTimestamp(),
      source,
      context,
      ipAddress,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null
    });

    return acceptanceId;
  } catch (error) {
    console.error('Error recording terms acceptance:', error);
    throw error;
  }
}

/**
 * Check if user needs to re-accept terms
 * @param {string} userTermsVersion - User's accepted terms version
 * @param {string} requiredVersion - Required terms version
 * @returns {boolean} True if user needs to re-accept
 */
export function needsReaccept(userTermsVersion, requiredVersion) {
  if (!userTermsVersion) return true;
  if (!requiredVersion) return false;

  // Compare semantic versions
  const userParts = userTermsVersion.split('.').map(Number);
  const requiredParts = requiredVersion.split('.').map(Number);

  for (let i = 0; i < Math.max(userParts.length, requiredParts.length); i++) {
    const userPart = userParts[i] || 0;
    const requiredPart = requiredParts[i] || 0;

    if (userPart < requiredPart) return true;
    if (userPart > requiredPart) return false;
  }

  return false; // Versions are equal
}

/**
 * Check user's terms status
 * @param {Object} userProfile - User profile from Firestore
 * @returns {Promise<Object>} Status object
 */
export async function checkUserTermsStatus(userProfile) {
  try {
    const config = await getCurrentTermsConfig();
    const userTermsVersion = userProfile?.termsAcceptedVersion;
    const userPrivacyVersion = userProfile?.privacyAcceptedVersion;

    return {
      currentVersion: config.currentVersion,
      requiredVersion: config.requiredVersion,
      privacyVersion: config.privacyVersion,
      userTermsVersion,
      userPrivacyVersion,
      needsTermsReaccept: needsReaccept(userTermsVersion, config.requiredVersion),
      needsPrivacyReaccept: needsReaccept(userPrivacyVersion, config.privacyVersion),
      termsAcceptedAt: userProfile?.termsAcceptedAt?.toDate?.() || null,
      privacyAcceptedAt: userProfile?.privacyAcceptedAt?.toDate?.() || null
    };
  } catch (error) {
    console.error('Error checking user terms status:', error);
    throw error;
  }
}

/**
 * Get user's acceptance history
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of acceptance records
 */
export async function getUserAcceptanceHistory(userId) {
  try {
    const q = query(
      collection(db, 'termsAcceptance'),
      where('userId', '==', userId),
      orderBy('acceptedAt', 'desc'),
      limit(50)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      acceptedAt: doc.data().acceptedAt?.toDate?.()
    }));
  } catch (error) {
    console.error('Error fetching acceptance history:', error);
    return [];
  }
}

/**
 * Update user profile with terms acceptance
 * @param {string} userId - User ID
 * @param {string} termsVersion - Terms version accepted
 * @param {string} privacyVersion - Privacy version accepted
 * @returns {Promise<void>}
 */
export async function updateUserTermsAcceptance(userId, termsVersion, privacyVersion) {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      termsAcceptedVersion: termsVersion,
      termsAcceptedAt: serverTimestamp(),
      privacyAcceptedVersion: privacyVersion,
      privacyAcceptedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating user terms acceptance:', error);
    throw error;
  }
}

/**
 * Create a new terms/privacy version (admin only)
 * @param {Object} data - Version data
 * @returns {Promise<string>} Version ID
 */
export async function createTermsVersion(data) {
  try {
    const { version, type, title, content, summary, effectiveDate } = data;
    const versionId = `${type}_v${version}`;

    // Get previous version
    const config = await getCurrentTermsConfig();
    const previousVersion = type === 'terms' ? config.currentVersion : config.privacyVersion;

    const versionRef = doc(db, 'termsVersions', versionId);
    await setDoc(versionRef, {
      version,
      type,
      title,
      content,
      summary: summary || '',
      effectiveDate: Timestamp.fromDate(new Date(effectiveDate)),
      createdAt: serverTimestamp(),
      previousVersion: previousVersion !== version ? previousVersion : null
    });

    return versionId;
  } catch (error) {
    console.error('Error creating terms version:', error);
    throw error;
  }
}

/**
 * Update site terms config (admin only)
 * @param {Object} config - New config values
 * @returns {Promise<void>}
 */
export async function updateTermsConfig(config) {
  try {
    const configRef = doc(db, 'siteConfig', 'terms');
    await setDoc(configRef, {
      ...config,
      lastUpdated: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating terms config:', error);
    throw error;
  }
}

/**
 * Get all terms versions for admin
 * @param {string} type - "terms" or "privacy"
 * @returns {Promise<Array>} Array of version objects
 */
export async function getAllTermsVersions(type = 'terms') {
  try {
    const q = query(
      collection(db, 'termsVersions'),
      where('type', '==', type),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      effectiveDate: doc.data().effectiveDate?.toDate?.(),
      createdAt: doc.data().createdAt?.toDate?.()
    }));
  } catch (error) {
    console.error('Error fetching all terms versions:', error);
    return [];
  }
}

export default {
  getCurrentTermsConfig,
  getTermsContent,
  getClientIP,
  recordTermsAcceptance,
  needsReaccept,
  checkUserTermsStatus,
  getUserAcceptanceHistory,
  updateUserTermsAcceptance,
  createTermsVersion,
  updateTermsConfig,
  getAllTermsVersions
};
